/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CpeField, FilterOperators } from "data/@types";

export interface AvailableAssetFiltersState {

   vendorsLoading: boolean;
   nextVendorsLoading: boolean;

   productsLoading: boolean;
   nextProductsLoading: boolean;

   availableAssetFiltersLoading: boolean;

   vendors: string[];
   selectedVendor: string;

   products: string[];
   selectedProduct: string;
   scheduledProductToSelect: string;

   availableFiltersLoadedFor: {
      vendor: string;
      product: string;
   }

   parts: string[];
   versions: string[];
   updates: string[];
   editions: string[];
   languages: string[];
   swEditions: string[];
   targetSWs: string[];
   targetHWs: string[];
   others: string[];

}

const initialState: AvailableAssetFiltersState = {

   vendorsLoading: false,
   nextVendorsLoading: false,

   productsLoading: false,
   nextProductsLoading: false,

   availableAssetFiltersLoading: false,

   vendors: [],
   selectedVendor: "",

   products: [],
   selectedProduct: "",
   scheduledProductToSelect: "",

   availableFiltersLoadedFor: {
      vendor: "",
      product: ""
   },

   parts: [],
   versions: [],
   updates: [],
   editions: [],
   languages: [],
   swEditions: [],
   targetSWs: [],
   targetHWs: [],
   others: []

}

export const slice = createSlice({

   name: "availableAssetFilters",

   initialState,

   reducers: {

      clearAvailableFilters: (state) => {
         state.availableFiltersLoadedFor = { vendor: "", product: "" };
         state.parts = [];
         state.versions = [];
         state.updates = [];
         state.editions = [];
         state.languages = [];
         state.swEditions = [];
         state.targetSWs = [];
         state.targetHWs = [];
         state.others = [];
      },

      selectVendor: (state, action: PayloadAction<{ vendor: string }>) => {
         state.selectedVendor = action.payload.vendor;
      },

      loadVendors: (state, action: PayloadAction<{ count?: number }>) => {
         state.vendorsLoading = true;
      },

      vendorsLoaded: (state, action: PayloadAction<{ vendors: string[] }>) => {
         state.vendorsLoading = false;
         state.vendors = action.payload.vendors;
         state.scheduledProductToSelect = "";
      },

      vendorsLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.vendorsLoading = false;
         console.error(action.payload.err);
      },

      loadNextVendors: (state, action: PayloadAction<{ count?: number }>) => {
         state.nextVendorsLoading = true;
      },

      nextVendorsLoaded: (state, action: PayloadAction<{ nextVendors: string[] }>) => {
         state.nextVendorsLoading = false;
         state.vendors = state.vendors.concat(action.payload.nextVendors);
      },

      nextVendorsLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.nextVendorsLoading = false;
         console.error(action.payload.err);
      },

      selectProduct: (state, action: PayloadAction<{ product: string }>) => {
         // value is set in setSelectedProduct instead because it must be set after async action
      },

      setSelectedProduct: (state, action: PayloadAction<{ product: string }>) => {
         state.selectedProduct = action.payload.product;
      },

      // this is to select product automatically, when vendors loading finishes
      scheduleSelectProduct: (state, action: PayloadAction<{ product: string }>) => {
         state.scheduledProductToSelect = action.payload.product;
      },

      clearProducts: (state) => {
         state.products = [];
      },

      loadProducts: (state, action: PayloadAction<{ count?: number }>) => {
         state.productsLoading = true;
      },

      productsLoaded: (state, action: PayloadAction<{ products: string[] }>) => {
         state.productsLoading = false;
         state.products = action.payload.products;
      },

      productsLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.nextProductsLoading = false;
         console.error(action.payload.err);
      },

      loadNextProducts: (state, action: PayloadAction<{ count?: number }>) => {
         state.nextProductsLoading = true;
      },

      nextProductsLoaded: (state, action: PayloadAction<{ nextProducts: string[] }>) => {
         state.nextProductsLoading = false;
         state.products = state.products.concat(action.payload.nextProducts);
      },

      nextProductsLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.nextProductsLoading = false;
         console.error(action.payload.err);
      },

      loadAvailableFilters: (state) => {
         state.availableAssetFiltersLoading = true;
         state.availableFiltersLoadedFor = { vendor: state.selectedVendor, product: state.selectedProduct }
      },

      availableFiltersLoaded: (state, action: PayloadAction<{ parts: string[][] }>) => {
         state.availableAssetFiltersLoading = false;

         state.parts = action.payload.parts[0].map<any>(v => {
            switch(v) {
               case "a": return "Application";
               case "o": return "Operating System";
               case "h": return "Hardware";
               default: return undefined;
            }
         });

         state.versions = action.payload.parts[1];
         state.updates = action.payload.parts[2];
         state.editions = action.payload.parts[3];
         state.languages = action.payload.parts[4];
         state.swEditions = action.payload.parts[5];
         state.targetSWs = action.payload.parts[6];
         state.targetHWs = action.payload.parts[7];
         state.others = action.payload.parts[8];
      },

      availableFiltersLoadingError: (state, action: PayloadAction<{ err: any }>) => {
         state.availableAssetFiltersLoading = false;
         console.error(action.payload.err);
      },

      addAvailableAssetFilterCriteria: (state, action: PayloadAction<{ field: CpeField, operator: FilterOperators, value: string, negate: boolean }>) => {
         // only to be able to process the action in cveBrowser epic
      }

   }

});

export const actions = slice.actions;

export default slice.reducer;